import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';

const EMPTY = {
  name: '', pin: '', hourlyRate: '10.50',
  hasCommission: false, commissionRate: '2', active: true,
  shopifyUserId: '',
};

function EmployeeForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-white/50 uppercase tracking-wide">Nombre completo *</label>
          <input value={form.name} onChange={e => set('name', e.target.value)}
            className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500/50"
            placeholder="Juan García" />
        </div>
        <div>
          <label className="text-xs text-white/50 uppercase tracking-wide">PIN (4 dígitos) *</label>
          <input value={form.pin} onChange={e => set('pin', e.target.value.replace(/\D/g, '').slice(0, 4))}
            maxLength={4} inputMode="numeric"
            className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono tracking-widest focus:outline-none focus:border-blue-500/50"
            placeholder="1234" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-white/50 uppercase tracking-wide">Tarifa por hora ($)</label>
          <input type="number" step="0.01" min="0" value={form.hourlyRate}
            onChange={e => set('hourlyRate', e.target.value)}
            className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500/50" />
        </div>
        <div>
          <label className="text-xs text-white/50 uppercase tracking-wide">Comisión (%)</label>
          <input type="number" step="0.5" min="0" max="100" value={form.commissionRate}
            onChange={e => set('commissionRate', e.target.value)}
            disabled={!form.hasCommission}
            className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500/50 disabled:opacity-30" />
        </div>
      </div>
      <div>
        <label className="text-xs text-white/50 uppercase tracking-wide">Shopify User ID</label>
        <input value={form.shopifyUserId || ''} onChange={e => set('shopifyUserId', e.target.value.replace(/\D/g, ''))}
          inputMode="numeric"
          className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-blue-500/50"
          placeholder="Ej: 82846777433 (de la orden POS en Shopify)" />
        <p className="text-white/25 text-[10px] mt-1">Shopify Admin → Pedidos POS → campo user_id</p>
      </div>
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-white/70">
          <input type="checkbox" checked={form.hasCommission} onChange={e => set('hasCommission', e.target.checked)}
            className="accent-blue-500 h-4 w-4" />
          Tiene comisión
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-white/70">
          <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)}
            className="accent-emerald-500 h-4 w-4" />
          Activo
        </label>
      </div>
      <div className="flex gap-2 pt-2">
        <button onClick={onCancel}
          className="flex-1 py-2 rounded-lg bg-white/5 border border-white/10 text-white/60 text-sm hover:bg-white/10 transition-colors">
          Cancelar
        </button>
        <button onClick={() => onSave(form)} disabled={saving}
          className="flex-1 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors disabled:opacity-50">
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}

export default function EmployeeManager({ isOpen, onClose }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null=list, 'add'=new, {id,...}=edit
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const unsub = onSnapshot(collection(db, 'sellers'), snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setEmployees(list);
      setLoading(false);
    });
    return unsub;
  }, [isOpen]);

  const handleSave = async (data) => {
    if (!data.name.trim()) { setError('El nombre es obligatorio'); return; }
    if (!data.pin || data.pin.length !== 4) { setError('El PIN debe tener exactamente 4 dígitos'); return; }
    // Check PIN uniqueness (except when editing same employee)
    const pinConflict = employees.find(e => e.pin === data.pin && e.id !== form?.id);
    if (pinConflict) { setError(`El PIN ${data.pin} ya está en uso por ${pinConflict.name}`); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: data.name.trim(),
        pin: data.pin,
        hourlyRate: parseFloat(data.hourlyRate) || 10.50,
        hasCommission: data.hasCommission,
        commissionRate: data.hasCommission ? (parseFloat(data.commissionRate) || 2) : 0,
        active: data.active,
        shopifyUserId: data.shopifyUserId?.trim() || null,
        updatedAt: serverTimestamp(),
      };
      if (form?.id) {
        await updateDoc(doc(db, 'sellers', form.id), payload);
      } else {
        await addDoc(collection(db, 'sellers'), { ...payload, createdAt: serverTimestamp() });
      }
      setForm(null);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteDoc(doc(db, 'sellers', deleteId));
      setDeleteId(null);
    } catch (err) {
      setError(err.message);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-gray-900 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-2xl">👥</span>
            <div>
              <h2 className="text-white font-bold text-lg">Empleados</h2>
              <p className="text-white/40 text-xs">{employees.length} registrado{employees.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!form && (
              <button onClick={() => { setForm(EMPTY); setError(''); }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors">
                + Agregar
              </button>
            )}
            <button onClick={onClose}
              className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
              ✕
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 bg-red-500/15 border border-red-500/30 text-red-300 rounded-lg px-4 py-3 text-sm">
              {error}
            </div>
          )}

          {/* Add/Edit form */}
          {form && (
            <div className="mb-6 bg-white/5 border border-white/10 rounded-xl p-4">
              <h3 className="text-white font-medium text-sm mb-4">
                {form.id ? `Editar: ${form.name}` : 'Nuevo empleado'}
              </h3>
              <EmployeeForm initial={form} onSave={handleSave} onCancel={() => { setForm(null); setError(''); }} saving={saving} />
            </div>
          )}

          {/* Employee list */}
          {loading ? (
            <div className="text-center text-white/30 py-8 text-sm">Cargando…</div>
          ) : employees.length === 0 ? (
            <div className="text-center text-white/25 py-12 text-sm">
              No hay empleados. ¡Agrega el primero!
            </div>
          ) : (
            <div className="space-y-2">
              {employees.map(emp => (
                <div key={emp.id}
                  className={`flex items-center gap-4 px-4 py-3 rounded-xl border transition-colors ${emp.active !== false ? 'border-white/8 bg-white/3 hover:bg-white/5' : 'border-white/5 bg-white/2 opacity-50'}`}>
                  {/* Avatar */}
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 border border-white/10 flex items-center justify-center text-sm font-bold text-white/70 shrink-0">
                    {(emp.name || '?').charAt(0).toUpperCase()}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">{emp.name}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-white/30 text-xs font-mono">PIN: {emp.pin || '—'}</span>
                      <span className="text-white/30 text-xs">${emp.hourlyRate || '—'}/h</span>
                      {emp.hasCommission && <span className="text-emerald-400/70 text-xs">{emp.commissionRate}% com.</span>}
                      {emp.shopifyUserId
                        ? <span className="text-blue-400/50 text-xs font-mono">ID: {emp.shopifyUserId}</span>
                        : <span className="text-amber-400/40 text-xs">Sin ID Shopify</span>}
                    </div>
                  </div>
                  {/* Status badge */}
                  <span className={`text-xs px-2 py-0.5 rounded-full border shrink-0 ${emp.active !== false ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25' : 'bg-white/5 text-white/30 border-white/10'}`}>
                    {emp.active !== false ? 'Activo' : 'Inactivo'}
                  </span>
                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => { setForm({ ...emp }); setError(''); }}
                      className="p-1.5 text-white/30 hover:text-white hover:bg-white/10 rounded-lg transition-colors text-xs">
                      ✏️
                    </button>
                    <button onClick={() => setDeleteId(emp.id)}
                      className="p-1.5 text-red-400/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors text-xs">
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-gray-900 border border-white/10 rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-white font-semibold mb-2">Confirmar eliminación</h3>
            <p className="text-white/50 text-sm mb-5">
              Esta acción es permanente. El historial de turnos del empleado se conservará.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)}
                className="flex-1 py-2 bg-white/5 border border-white/10 text-white/60 rounded-lg text-sm hover:bg-white/10">
                Cancelar
              </button>
              <button onClick={handleDelete}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium">
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
